import { InteractivePythonBlock } from '../components/ui/InteractivePythonBlock';

export default function PortfolioManagementModernPortfolioTheory() {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-blue-600 text-white py-12">
        <div className="max-w-4xl mx-auto px-6">
          <h1 className="text-4xl font-bold mb-4">Modern Portfolio Theory</h1>
          <p className="text-xl opacity-90">Portfolio Management Series</p>
          <div className="mt-6 flex items-center space-x-4">
            <span className="bg-blue-700 px-3 py-1 rounded-full text-sm">Interactive Python</span>
            <span className="bg-blue-700 px-3 py-1 rounded-full text-sm">Professional Level</span>
            <span className="bg-blue-700 px-3 py-1 rounded-full text-sm">Learn Library</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        
        {/* Introduction */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Introduction to Modern Portfolio Theory</h2>
          <div className="prose prose-lg max-w-none">
            <p className="text-gray-700 text-lg leading-relaxed mb-6">
              Modern Portfolio Theory (MPT), developed by Harry Markowitz in 1952, revolutionized investment management by providing a mathematical framework for constructing optimal portfolios. The theory demonstrates how investors can maximize expected returns for a given level of risk, or minimize risk for a given level of expected return.
            </p>
            <p className="text-gray-700 text-lg leading-relaxed mb-6">
              The key insight of MPT is that the risk of a portfolio is not simply the weighted average of the risks of individual assets. Instead, it depends on the correlations between assets, allowing for diversification benefits that can reduce overall portfolio risk.
            </p>
          </div>
        </section>

        {/* Core Concepts */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Core Concepts</h2>
          
          <div className="bg-blue-50 border-l-4 border-blue-500 p-6 mb-8">
            <h3 className="text-xl font-bold text-blue-900 mb-3">Expected Return</h3>
            <p className="text-blue-800">
              The expected return of a portfolio is the weighted average of the expected returns of individual assets:
            </p>
            <div className="bg-white p-4 rounded mt-3 font-mono text-sm">
              E(Rp) = Σ wi × E(Ri)
            </div>
          </div>

          <div className="bg-green-50 border-l-4 border-green-500 p-6 mb-8">
            <h3 className="text-xl font-bold text-green-900 mb-3">Portfolio Variance</h3>
            <p className="text-green-800">
              Portfolio variance accounts for individual asset variances and covariances:
            </p>
            <div className="bg-white p-4 rounded mt-3 font-mono text-sm">
              σp² = Σ Σ wi × wj × σij
            </div>
          </div>

          <div className="bg-purple-50 border-l-4 border-purple-500 p-6 mb-8">
            <h3 className="text-xl font-bold text-purple-900 mb-3">Diversification Benefit</h3>
            <p className="text-purple-800">
              When assets are not perfectly correlated (ρ &lt; 1), diversification reduces portfolio risk below the weighted average of individual asset risks.
            </p>
          </div>
        </section>

        {/* Interactive Example 1: Basic Portfolio Calculation */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Interactive Example: Basic Portfolio Risk Calculation</h2>
          <p className="text-gray-700 text-lg mb-6">
            Let's calculate the expected return, variance, and risk contributions for a simple three-asset portfolio:
          </p>
          
          <InteractivePythonBlock 
            filename="basic_portfolio_risk.py" 
            title="Portfolio Risk Analysis"
            editable={true}
          >
{`import numpy as np

print("=== MODERN PORTFOLIO THEORY: BASIC EXAMPLE ===")
print()

# Portfolio composition
assets = ['Stock A', 'Stock B', 'Stock C']
returns = np.array([0.08, 0.12, 0.15])      # Expected returns: 8%, 12%, 15%
weights = np.array([0.4, 0.35, 0.25])       # Portfolio weights: 40%, 35%, 25%
volatilities = np.array([0.16, 0.20, 0.25]) # Asset volatilities: 16%, 20%, 25%

# Correlation matrix
correlation_matrix = np.array([
    [1.0, 0.6, 0.4],  # Stock A correlations
    [0.6, 1.0, 0.5],  # Stock B correlations  
    [0.4, 0.5, 1.0]   # Stock C correlations
])

print("Portfolio Composition:")
for i, asset in enumerate(assets):
    print(f"{asset}: {weights[i]:.1%} weight, {returns[i]:.1%} expected return, {volatilities[i]:.1%} volatility")
print()

# Calculate portfolio expected return
portfolio_return = np.sum(weights * returns)
print(f"Portfolio Expected Return: {portfolio_return:.2%}")

# Calculate covariance matrix
covariance_matrix = np.outer(volatilities, volatilities) * correlation_matrix
print("\\nCovariance Matrix:")
print(covariance_matrix)

# Calculate portfolio variance and volatility
portfolio_variance = np.dot(weights, np.dot(covariance_matrix, weights))
portfolio_volatility = np.sqrt(portfolio_variance)

print(f"\\nPortfolio Variance: {portfolio_variance:.6f}")
print(f"Portfolio Volatility: {portfolio_volatility:.2%}")

# Calculate Sharpe ratio (assuming 3% risk-free rate)
risk_free_rate = 0.03
sharpe_ratio = (portfolio_return - risk_free_rate) / portfolio_volatility
print(f"Sharpe Ratio: {sharpe_ratio:.3f}")

# Risk contribution analysis
marginal_contributions = np.dot(covariance_matrix, weights)
risk_contributions = weights * marginal_contributions / portfolio_variance
percentage_contributions = risk_contributions / np.sum(risk_contributions)

print("\\n=== RISK CONTRIBUTION ANALYSIS ===")
for i, asset in enumerate(assets):
    print(f"{asset}: {percentage_contributions[i]:.1%} of total risk")

print("\\n✅ Try modifying the weights, returns, or correlations above!")
print("💡 Notice how diversification reduces risk below the weighted average volatility")`}
          </InteractivePythonBlock>
        </section>

        {/* Interactive Example 2: Efficient Frontier */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Interactive Example: Efficient Frontier Construction</h2>
          <p className="text-gray-700 text-lg mb-6">
            The efficient frontier represents the set of optimal portfolios offering the highest expected return for each level of risk:
          </p>
          
          <InteractivePythonBlock 
            filename="efficient_frontier.py" 
            title="Efficient Frontier Analysis"
            editable={true}
          >
{`import numpy as np
from scipy.optimize import minimize

print("=== EFFICIENT FRONTIER CONSTRUCTION ===")
print()

# Asset data
returns = np.array([0.08, 0.12, 0.15])      # Expected returns
volatilities = np.array([0.16, 0.20, 0.25]) # Volatilities
correlation_matrix = np.array([
    [1.0, 0.6, 0.4],
    [0.6, 1.0, 0.5], 
    [0.4, 0.5, 1.0]
])

# Calculate covariance matrix
cov_matrix = np.outer(volatilities, volatilities) * correlation_matrix

def portfolio_stats(weights, returns, cov_matrix):
    """Calculate portfolio return and volatility"""
    port_return = np.sum(weights * returns)
    port_variance = np.dot(weights, np.dot(cov_matrix, weights))
    port_volatility = np.sqrt(port_variance)
    return port_return, port_volatility

def minimize_volatility(target_return, returns, cov_matrix):
    """Find minimum volatility portfolio for target return"""
    n_assets = len(returns)
    
    # Objective function: minimize portfolio variance
    def objective(weights):
        return np.dot(weights, np.dot(cov_matrix, weights))
    
    # Constraints
    constraints = [
        {'type': 'eq', 'fun': lambda w: np.sum(w) - 1},  # Weights sum to 1
        {'type': 'eq', 'fun': lambda w: np.sum(w * returns) - target_return}  # Target return
    ]
    
    # Bounds (no short selling)
    bounds = [(0, 1) for _ in range(n_assets)]
    
    # Initial guess (equal weights)
    initial_guess = np.array([1/n_assets] * n_assets)
    
    # Optimize
    result = minimize(objective, initial_guess, method='SLSQP', 
                     bounds=bounds, constraints=constraints)
    
    if result.success:
        return result.x
    else:
        return None

# Calculate efficient frontier points
target_returns = np.linspace(0.08, 0.15, 8)
efficient_portfolios = []

print("Efficient Frontier Portfolios:")
print("Target Return | Volatility | Stock A | Stock B | Stock C")
print("-" * 60)

for target_ret in target_returns:
    weights = minimize_volatility(target_ret, returns, cov_matrix)
    if weights is not None:
        port_ret, port_vol = portfolio_stats(weights, returns, cov_matrix)
        efficient_portfolios.append((port_ret, port_vol, weights))
        print(f"{port_ret:>12.1%} | {port_vol:>9.1%} | {weights[0]:>6.1%} | {weights[1]:>6.1%} | {weights[2]:>6.1%}")

# Find minimum variance portfolio
print("\\n=== SPECIAL PORTFOLIOS ===")

# Minimum variance portfolio (no return constraint)
def min_var_objective(weights):
    return np.dot(weights, np.dot(cov_matrix, weights))

min_var_constraints = [{'type': 'eq', 'fun': lambda w: np.sum(w) - 1}]
min_var_result = minimize(min_var_objective, [1/3, 1/3, 1/3], 
                         method='SLSQP', bounds=[(0, 1)] * 3, 
                         constraints=min_var_constraints)

if min_var_result.success:
    min_var_weights = min_var_result.x
    min_var_ret, min_var_vol = portfolio_stats(min_var_weights, returns, cov_matrix)
    print(f"Minimum Variance Portfolio:")
    print(f"  Return: {min_var_ret:.2%}, Volatility: {min_var_vol:.2%}")
    print(f"  Weights: A={min_var_weights[0]:.1%}, B={min_var_weights[1]:.1%}, C={min_var_weights[2]:.1%}")

# Maximum Sharpe ratio portfolio
risk_free_rate = 0.03
def neg_sharpe_ratio(weights):
    port_ret, port_vol = portfolio_stats(weights, returns, cov_matrix)
    return -(port_ret - risk_free_rate) / port_vol

sharpe_constraints = [{'type': 'eq', 'fun': lambda w: np.sum(w) - 1}]
sharpe_result = minimize(neg_sharpe_ratio, [1/3, 1/3, 1/3], 
                        method='SLSQP', bounds=[(0, 1)] * 3, 
                        constraints=sharpe_constraints)

if sharpe_result.success:
    max_sharpe_weights = sharpe_result.x
    max_sharpe_ret, max_sharpe_vol = portfolio_stats(max_sharpe_weights, returns, cov_matrix)
    max_sharpe_ratio = (max_sharpe_ret - risk_free_rate) / max_sharpe_vol
    print(f"\\nMaximum Sharpe Ratio Portfolio:")
    print(f"  Return: {max_sharpe_ret:.2%}, Volatility: {max_sharpe_vol:.2%}")
    print(f"  Sharpe Ratio: {max_sharpe_ratio:.3f}")
    print(f"  Weights: A={max_sharpe_weights[0]:.1%}, B={max_sharpe_weights[1]:.1%}, C={max_sharpe_weights[2]:.1%}")

print("\\n✅ Try modifying the expected returns or correlations!")
print("💡 The efficient frontier shows the best risk-return trade-offs available")`}
          </InteractivePythonBlock>
        </section>

        {/* Interactive Example 3: Black-Litterman Model */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Advanced Example: Black-Litterman Model</h2>
          <p className="text-gray-700 text-lg mb-6">
            The Black-Litterman model improves on traditional MPT by incorporating market equilibrium assumptions and investor views:
          </p>
          
          <InteractivePythonBlock 
            filename="black_litterman.py" 
            title="Black-Litterman Model"
            editable={true}
          >
{`import numpy as np

print("=== BLACK-LITTERMAN MODEL ===")
print()

# Market equilibrium data
market_caps = np.array([1000, 800, 600])  # Market capitalizations (billions)
market_weights = market_caps / np.sum(market_caps)  # Market cap weights
risk_aversion = 3.0  # Risk aversion parameter
risk_free_rate = 0.03

# Asset data
volatilities = np.array([0.16, 0.20, 0.25])
correlation_matrix = np.array([
    [1.0, 0.6, 0.4],
    [0.6, 1.0, 0.5], 
    [0.4, 0.5, 1.0]
])
cov_matrix = np.outer(volatilities, volatilities) * correlation_matrix

print("Market Equilibrium Setup:")
print(f"Market Cap Weights: {market_weights}")
print(f"Risk Aversion (λ): {risk_aversion}")
print(f"Risk-free Rate: {risk_free_rate:.1%}")
print()

# Step 1: Calculate implied equilibrium returns
# π = λ × Σ × w_market
implied_returns = risk_aversion * np.dot(cov_matrix, market_weights)
print("Implied Equilibrium Returns:")
for i, ret in enumerate(implied_returns):
    print(f"Asset {i+1}: {ret:.2%}")
print()

# Step 2: Define investor views
# View 1: Asset 1 will outperform Asset 2 by 2%
# View 2: Asset 3 will have absolute return of 18%

# P matrix: picking matrix (which assets the views relate to)
P = np.array([
    [1, -1, 0],   # Asset 1 - Asset 2
    [0,  0, 1]    # Asset 3 absolute
])

# Q vector: view returns
Q = np.array([0.02, 0.18])  # 2% relative, 18% absolute

# Ω matrix: uncertainty in views (diagonal matrix)
view_uncertainty = np.array([0.0004, 0.0025])  # 2% and 5% standard errors
Omega = np.diag(view_uncertainty)

print("Investor Views:")
print("View 1: Asset 1 will outperform Asset 2 by 2.0% (±2.0% uncertainty)")
print("View 2: Asset 3 will return 18.0% (±5.0% uncertainty)")
print()

# Step 3: Calculate tau (scales the uncertainty of the prior)
tau = 1 / len(implied_returns)  # Common choice: 1/n
print(f"Tau parameter: {tau:.3f}")
print()

# Step 4: Black-Litterman formula
# μ_BL = [(τΣ)^(-1) + P'Ω^(-1)P]^(-1) × [(τΣ)^(-1)π + P'Ω^(-1)Q]

# Calculate components
tau_cov_inv = np.linalg.inv(tau * cov_matrix)
P_omega_inv_P = np.dot(P.T, np.dot(np.linalg.inv(Omega), P))
first_term = np.linalg.inv(tau_cov_inv + P_omega_inv_P)

tau_cov_inv_pi = np.dot(tau_cov_inv, implied_returns)
P_omega_inv_Q = np.dot(P.T, np.dot(np.linalg.inv(Omega), Q))
second_term = tau_cov_inv_pi + P_omega_inv_Q

# Black-Litterman expected returns
bl_returns = np.dot(first_term, second_term)

# Black-Litterman covariance matrix
bl_cov = first_term

print("Black-Litterman Results:")
print("Expected Returns:")
for i, (eq_ret, bl_ret) in enumerate(zip(implied_returns, bl_returns)):
    change = bl_ret - eq_ret
    print(f"Asset {i+1}: {eq_ret:.2%} → {bl_ret:.2%} (change: {change:+.2%})")
print()

# Calculate optimal portfolio weights
# w* = (λ × Σ_BL)^(-1) × μ_BL
bl_weights = np.dot(np.linalg.inv(risk_aversion * bl_cov), bl_returns)

print("Portfolio Weights:")
print("Asset      | Market | Black-Litterman | Change")
print("-" * 50)
for i, (mkt_w, bl_w) in enumerate(zip(market_weights, bl_weights)):
    change = bl_w - mkt_w
    print(f"Asset {i+1:>2}   | {mkt_w:>5.1%}  | {bl_w:>13.1%}   | {change:+6.1%}")

# Portfolio statistics
bl_port_return = np.sum(bl_weights * bl_returns)
bl_port_variance = np.dot(bl_weights, np.dot(bl_cov, bl_weights))
bl_port_volatility = np.sqrt(bl_port_variance)
bl_sharpe = (bl_port_return - risk_free_rate) / bl_port_volatility

market_port_return = np.sum(market_weights * implied_returns)
market_port_variance = np.dot(market_weights, np.dot(cov_matrix, market_weights))
market_port_volatility = np.sqrt(market_port_variance)
market_sharpe = (market_port_return - risk_free_rate) / market_port_volatility

print("\\nPortfolio Performance:")
print(f"Market Portfolio:  Return={market_port_return:.2%}, Vol={market_port_volatility:.2%}, Sharpe={market_sharpe:.3f}")
print(f"B-L Portfolio:     Return={bl_port_return:.2%}, Vol={bl_port_volatility:.2%}, Sharpe={bl_sharpe:.3f}")

print("\\n✅ Try modifying the investor views (Q vector) or uncertainties (Omega)!")
print("💡 Black-Litterman combines market equilibrium with your specific views")`}
          </InteractivePythonBlock>
        </section>

        {/* Key Takeaways */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Key Takeaways</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3">🎯 Core Principles</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Diversification reduces risk without sacrificing return</li>
                <li>• Correlation is key to diversification benefits</li>
                <li>• Efficient portfolios maximize return for given risk</li>
                <li>• Risk-return trade-offs are fundamental</li>
              </ul>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3">⚠️ Limitations</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Assumes normal return distributions</li>
                <li>• Requires accurate return/risk estimates</li>
                <li>• Static, single-period framework</li>
                <li>• Ignores transaction costs and constraints</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Purchase CTA */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-8 rounded-lg text-center">
          <h3 className="text-2xl font-bold mb-4">Master Modern Portfolio Theory</h3>
          <p className="text-lg opacity-90 mb-6">
            Get the complete interactive module with advanced examples, real-world case studies, and downloadable Python code.
          </p>
          <button className="bg-white text-blue-600 px-8 py-3 rounded-lg font-bold text-lg hover:bg-gray-100 transition-colors">
            Get access via Learn Library — /subscribe
          </button>
        </div>
      </div>
    </div>
  );
}